import React, {useCallback, useMemo} from 'react';
import {useHistory} from 'react-router-dom';

import {useWeaveflowCurrentRouteContext} from '../../context';
import {useURLSearchParamsDict} from '../util';
import {CompareEvaluationsPage} from './CompareEvaluationsPage';
import {ScoreFilter} from './ecpState';

type CompareEvaluationsPageLoadViewProps = {
  entity: string;
  project: string;
};

export const CompareEvaluationsPageLoadView = ({
  entity,
  project,
}: CompareEvaluationsPageLoadViewProps) => {
  const query = useURLSearchParamsDict();
  const history = useHistory();
  const routerContext = useWeaveflowCurrentRouteContext();

  const evaluationCallIds = useMemo(() => {
    return JSON.parse(query.evaluationCallIds);
  }, [query.evaluationCallIds]);

  const selectedMetrics: Record<string, boolean> | null = useMemo(() => {
    try {
      return JSON.parse(query.metrics);
    } catch (e) {
      return null;
    }
  }, [query.metrics]);

  const scoreFilters: ScoreFilter[] = useMemo(() => {
    try {
      return JSON.parse(query.scoreFilters);
    } catch (e) {
      return [];
    }
  }, [query.scoreFilters]);

  const initialTabValue = useMemo(() => {
    const tab = query.tab;
    if (tab === 'summary' || tab === 'results') {
      return tab;
    }
    return undefined;
  }, [query.tab]);

  const onEvaluationCallIdsUpdate = useCallback(
    (newEvaluationCallIds: string[]) => {
      history.push(
        routerContext.compareEvaluationsUri(
          entity,
          project,
          newEvaluationCallIds,
          selectedMetrics,
          scoreFilters,
          initialTabValue
        )
      );
    },
    [
      history,
      entity,
      project,
      routerContext,
      selectedMetrics,
      scoreFilters,
      initialTabValue,
    ]
  );

  const setSelectedMetrics = useCallback(
    (newModel: Record<string, boolean>) => {
      history.push(
        routerContext.compareEvaluationsUri(
          entity,
          project,
          evaluationCallIds,
          newModel,
          scoreFilters,
          initialTabValue
        )
      );
    },
    [
      history,
      entity,
      project,
      routerContext,
      evaluationCallIds,
      scoreFilters,
      initialTabValue,
    ]
  );

  const setScoreFilters = useCallback(
    (newFilters: ScoreFilter[]) => {
      history.push(
        routerContext.compareEvaluationsUri(
          entity,
          project,
          evaluationCallIds,
          selectedMetrics,
          newFilters,
          initialTabValue
        )
      );
    },
    [
      history,
      entity,
      project,
      routerContext,
      evaluationCallIds,
      selectedMetrics,
      initialTabValue,
    ]
  );

  const onTabChange = useCallback(
    (tab: 'summary' | 'results') => {
      history.push(
        routerContext.compareEvaluationsUri(
          entity,
          project,
          evaluationCallIds,
          selectedMetrics,
          scoreFilters,
          tab
        )
      );
    },
    [
      history,
      entity,
      project,
      routerContext,
      evaluationCallIds,
      selectedMetrics,
      scoreFilters,
    ]
  );

  return (
    <CompareEvaluationsPage
      entity={entity}
      project={project}
      evaluationCallIds={evaluationCallIds}
      onEvaluationCallIdsUpdate={onEvaluationCallIdsUpdate}
      selectedMetrics={selectedMetrics}
      setSelectedMetrics={setSelectedMetrics}
      scoreFilters={scoreFilters}
      setScoreFilters={setScoreFilters}
      initialTabValue={initialTabValue}
      onTabChange={onTabChange}
    />
  );
};
